import { useEffect, useState } from 'react';
import type { AuthError } from '@supabase/supabase-js';
import { LINK_ERROR, supabase } from '../lib/supabase';
import { Btn, Field, Panel } from '../ui/kit';

/**
 * Every person has a login. A person signs in with a link sent to their email; there is no
 * password to keep. An AI employee has no inbox to open a link from, so it signs in with the
 * key the owner issued it under Settings → Users (one per employee; a new key replaces the old).
 *
 * The screen says in plain words why a link did not go out (Spencer, 2026-10-15): the sender
 * behind the published app allows two links an hour and reaches only the owner's address until
 * an email sender is set up (docs/publishing.md).
 */

/** Why a link did not go out, in the words of someone who only wants to get in. */
function whyNotSent(e: AuthError): string {
  switch (e.code) {
    case 'over_email_send_rate_limit':
      return 'Two sign-in links have gone out in the last hour, and that is the sender’s limit. Open the last link you were sent if it is under an hour old (it works once); otherwise ask again in an hour.';
    case 'over_request_rate_limit':
      return 'Too many tries from this device. Wait a few minutes, then ask once.';
    case 'email_address_not_authorized':
      return 'ZeroGap’s email sender cannot reach this address yet: only the owner’s address works until an email sender is set up.';
    default:
      return `The link could not be sent (${e.message}). Try again in a few minutes.`;
  }
}

/** What a link that brought the browser back with an error means. */
function linkTrouble(): string | null {
  if (!LINK_ERROR) return null;
  if (LINK_ERROR.code === 'otp_expired') return 'That sign-in link has expired or was already used. Each link works once, for an hour. Ask for a new one.';
  return `That sign-in link did not work${LINK_ERROR.description ? ` (${LINK_ERROR.description})` : ''}. Ask for a new one.`;
}

export function SignIn() {
  const [way, setWay] = useState<'link' | 'key'>('link');
  const [email, setEmail] = useState('');
  const [key, setKey] = useState('');
  const [state, setState] = useState<'idle' | 'busy' | 'sent' | 'error'>(() => (linkTrouble() ? 'error' : 'idle'));
  const [message, setMessage] = useState(() => linkTrouble() ?? '');
  // the link's error is read once; the address goes back to the app's own
  useEffect(() => {
    if (LINK_ERROR) window.history.replaceState(null, '', window.location.pathname + window.location.search);
  }, []);
  const fail = (m: string) => {
    setMessage(m);
    setState('error');
  };
  const sendLink = async () => {
    if (!email.trim()) return;
    setState('busy');
    const { error } = await supabase.auth.signInWithOtp({ email: email.trim(), options: { emailRedirectTo: window.location.origin, shouldCreateUser: true } });
    if (error) fail(whyNotSent(error));
    else setState('sent');
  };
  const useKey = async () => {
    if (!email.trim() || !key.trim()) return;
    setState('busy');
    const { error } = await supabase.auth.signInWithPassword({ email: email.trim().toLowerCase(), password: key.trim() });
    // success is not handled here: the session provider sees the new session and the app opens
    if (error) fail(error.message === 'Invalid login credentials' ? 'That email and key do not match. A key is issued under Settings → Users; a new one replaces the old.' : error.message);
    else setState('idle');
  };
  const swap = (w: 'link' | 'key') => {
    setWay(w);
    setState('idle');
    setMessage('');
  };
  return (
    <div className="signin">
      <h1 style={{ marginBottom: 12 }}>ZeroGap</h1>
      <Panel label={way === 'link' ? 'Sign in' : 'Sign in with a key'}>
        {state === 'sent' ? (
          <div className="stack">
            <p>
              A sign-in link is on its way to <b>{email.trim()}</b>. Open it on this device within the hour; it works once.
            </p>
            <p>
              <small>Nothing after a minute? Look in the spam folder, then ask again.</small>
            </p>
            <p>
              <small>
                <button type="button" className="linklike" onClick={() => setState('idle')}>
                  Ask again, or use a different email
                </button>
              </small>
            </p>
          </div>
        ) : (
          <form
            className="stack"
            onSubmit={(e) => {
              e.preventDefault();
              void (way === 'link' ? sendLink() : useKey());
            }}
          >
            <Field label="Email" read>
              <input type="email" value={email} autoFocus autoComplete="username" onChange={(e) => setEmail(e.target.value)} />
            </Field>
            {way === 'key' && (
              <Field label="Key" read>
                <input type="password" value={key} autoComplete="current-password" onChange={(e) => setKey(e.target.value)} />
              </Field>
            )}
            <Btn primary read type="submit" disabled={state === 'busy'}>
              {way === 'link' ? 'Email me a sign-in link' : 'Sign in'}
            </Btn>
            {state === 'error' && <p className="bad">{message}</p>}
          </form>
        )}
        <p style={{ marginTop: 12 }}>
          <small>
            {way === 'link' ? (
              <>
                AI employee?{' '}
                <button type="button" className="linklike" onClick={() => swap('key')}>
                  Sign in with your key
                </button>
                .
              </>
            ) : (
              <>
                A person signs in by email instead:{' '}
                <button type="button" className="linklike" onClick={() => swap('link')}>
                  email me a link
                </button>
                .
              </>
            )}
          </small>
        </p>
      </Panel>
    </div>
  );
}

export function NotSetUp({ onSignOut }: { onSignOut: () => void }) {
  return (
    <div className="signin">
      <h1 style={{ marginBottom: 12 }}>ZeroGap</h1>
      <Panel label="Not set up yet">
        <p style={{ marginBottom: 10 }}>You are signed in, but no one has added you under Settings → Users yet. Ask the owner to add your email there.</p>
        <Btn read onClick={onSignOut}>Sign out</Btn>
      </Panel>
    </div>
  );
}
